<template>
	<Pupup
		:showClose="true"
		title="Answer three quick questions and we'll point you in the right direction."
		:width="900"
		:full="false"
		:loading="loading"
		:clickOverlayToClose="false"
		@close="handleClose">
		<template #content>
			<div class="idea-quiz">
				<div v-if="errorMessage" class="quiz-error">{{ errorMessage }}</div>

				<div v-if="quizReady" class="quiz-body">
					<div v-if="view === 'quiz' && currentQuestion" class="quiz-question">
						<div class="question-meta">
							<span class="question-count">Question {{ currentIndex + 1 }} of {{ totalQuestions }}</span>
							<span class="question-rule">{{ selectionLabel }}</span>
						</div>
						<h3 class="question-title">{{ currentQuestion.question_text }}</h3>
						<p class="question-description" v-if="currentQuestion.description">{{ currentQuestion.description }}</p>

						<div class="responses">
							<button
								v-for="response in responsesForCurrent"
								:key="'response-' + response.id"
								type="button"
								:class="{ selected: isSelected(currentQuestion.id, response.id), 'response-option': true,'unsure': response.stop_after_response&&response.conditional_results&&response.conditional_results.length&&response.conditional_results[0].title==='unsure' }"
								@click="toggleResponse(currentQuestion.id, response.id, response.stop_after_response&&response.conditional_results&&response.conditional_results.length&&response.conditional_results[0].title==='unsure')">
								<span class="option-label">{{ response.response_text }}</span>
								<span v-if="response.stop_after_response&&response.conditional_results&&response.conditional_results.length&&response.conditional_results[0].title==='unsure'">
									<span class="button quiz-btn secondary" @click.stop="openCollabGuide">Read our Collab Guide</span>
									<span class="button quiz-btn secondary" @click="goToPitch">Show me opportunities other brand have posted</span>
								</span>
								<span class="option-check" v-if="isSelected(currentQuestion.id, response.id)">✓</span>
							</button>
						</div>
					</div>

					<div v-else class="quiz-results">
						<div class="results-header">
							<h3>Your collaboration ideas</h3>
							<p>Click for more information on each of them.</p>
						</div>

						<div v-if="hasResults">
							<div v-if="showCollabTypes" class="results-cards">
								<CollabTypes :mini="true" :showTCs="false" :filterByIds="collabIds" :filterByTitles="legacyTitles" />
							</div>
							<div v-else class="no-results">We could not find matching ideas yet.</div>

							<div v-if="legacyTitles.length" class="legacy-results">
								<p class="legacy-title">Other suggested ideas</p>
								<div class="legacy-tags">
									<span v-for="title in legacyTitles" :key="title" class="legacy-tag">{{ title }}</span>
								</div>
							</div>
						</div>
						<div v-else class="no-results">We could not find matching ideas yet.</div>

						<div class="results-actions">
							<button v-if="hasPitch" type="button" class="button quiz-btn primary" @click="goToPitch">
								Head to the Opportunities Area
							</button>
						</div>
					</div>
				</div>

				<div v-else class="quiz-empty">
					<p>Quiz unavailable right now.</p>
					<button type="button" class="button quiz-btn secondary" @click="handleClose">Close</button>
				</div>
			</div>
		</template>

		<template #footer-inner>
			<div class="quiz-footer buttons" v-if="view === 'quiz'">
				<button type="button" class="button quiz-btn secondary" @click="prevQuestion" :disabled="currentIndex === 0">Back</button>
				<div class="quiz-stepper" v-if="view === 'quiz'">
					<span
						v-for="(question, index) in questions"
						:key="'step-' + question.id"
						:class="{ 'step-dot': true, active: index === currentIndex, done: index < currentIndex }"></span>
				</div>
				<button type="button" class="button quiz-btn primary" @click="nextQuestion" :disabled="!canContinue">
					{{ isLastQuestion ? "See results" : "Next" }}
				</button>
			</div>
			<div class="quiz-footer" v-else>
				<button type="button" class="button quiz-btn secondary" @click="resetQuiz">Take the quiz again</button>
				<button type="button" class="button quiz-btn secondary" @click="handleClose">Close</button>
			</div>
		</template>
	</Pupup>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import Pupup from "../Display/Pupup.vue";
import CollabTypes from "../Display/CollabTypes.vue";
import ApiService from "./../../services/api.service";
import { useVueInstance } from "../../composables/useVueInstance";
import general from "../../utils/general";

export default {
	name: "IdeaQuiz",
	components: {
		Pupup,
		CollabTypes,
	},
	props: {
		quizId: {
			type: [String, Number],
			default: null,
		},
	},
	emits: ["close"],
	setup(props, { emit }) {
		const loading = ref(false);
		const router = useRouter();
		const { store } = useVueInstance();
		const errorMessage = ref("");
		const quiz = ref(null);
		const questions = ref([]);
		const responsesByQuestion = ref({});
		const currentIndex = ref(0);
		const answers = ref({});
		const results = ref([]);
		const view = ref("quiz");

		const quizReady = computed(() => !!quiz.value && questions.value.length > 0);
		const totalQuestions = computed(() => questions.value.length);
		const currentQuestion = computed(() => questions.value[currentIndex.value] || null);
		const responsesForCurrent = computed(() => {
			if (!currentQuestion.value) return [];
			return responsesByQuestion.value[currentQuestion.value.id] || [];
		});

		const selectionLabel = computed(() => {
			const question = currentQuestion.value;
			if (!question) return "";
			const min = Number(question.min_responses || 1);
			let max = Number(question.max_responses || min || 1);
			if (!max || max < min) max = min || 1;

			if (min === 0 && max > 0) {
				return `Pick up to ${max} option${max > 1 ? "s" : ""}`;
			}

			if (min === max) {
				return `Pick ${min} option${min > 1 ? "s" : ""}`;
			}

			return `Pick ${min} - ${max} options`;
		});

		const isLastQuestion = computed(() => currentIndex.value >= totalQuestions.value - 1);

		const isQuestionValid = (question) => {
			const selected = answers.value[question.id] || [];
			const min = Number(question.min_responses || 1);
			let max = Number(question.max_responses || min || 1);
			if (!max || max < min) max = min || 1;

			return selected.length >= min && selected.length <= max;
		};

		const canContinue = computed(() => {
			if (!currentQuestion.value) return false;
			return isQuestionValid(currentQuestion.value);
		});

		const hasResults = computed(() => results.value.length > 0);

		const collabIds = computed(() => {
			const ids = new Set();
			results.value.forEach((result) => {
				if (result && result.type === "collab" && result.id !== null && typeof result.id !== "undefined" && result.id !== "") {
					ids.add(String(result.id));
				}
			});
			return Array.from(ids);
		});

		const legacyTitles = computed(() => {
			const titles = new Set();
			results.value.forEach((result) => {
				if (result && result.type === "legacy-title" && result.title) {
					titles.add(String(result.title));
				}
			});
			return Array.from(titles);
		});

		const hasPitch = computed(() => results.value.some((result) => result && result.type === "pitch"));
		const showCollabTypes = computed(() => collabIds.value.length > 0 || legacyTitles.value.length > 0);

		const initializeAnswers = (questionList) => {
			const next = {};
			questionList.forEach((question) => {
				next[question.id] = [];
			});
			answers.value = next;
		};

		const handleClose = () => {
			emit("close");
		};

		const loadQuiz = async (quizId) => {
			loading.value = "Loading quiz...";
			errorMessage.value = "";
			view.value = "quiz";
			results.value = [];
			currentIndex.value = 0;

			try {
				const uri = quizId ? `quiz/v2/${quizId}` : "quiz/v2";
				const info = await ApiService.get(uri);
				const data = info && info.data ? info.data : {};

				quiz.value = data.quiz || null;

				const questionList = Array.isArray(data.questions) ? data.questions.slice() : [];
				questionList.sort((a, b) => (a.order_num || 0) - (b.order_num || 0));
				questions.value = questionList;

				const responseMap = data.responses || {};
				Object.keys(responseMap).forEach((questionId) => {
					responseMap[questionId] = responseMap[questionId].slice().sort((a, b) => (a.order_num || 0) - (b.order_num || 0));
				});
				responsesByQuestion.value = responseMap;

				initializeAnswers(questionList);
			} catch (err) {
				console.error("IdeaQuiz.load", err);
				errorMessage.value = "We could not load the quiz right now.";
				quiz.value = null;
				questions.value = [];
				responsesByQuestion.value = {};
			} finally {
				loading.value = false;
			}
		};

		const isSelected = (questionId, responseId) => {
			const selected = answers.value[questionId] || [];
			return selected.some((value) => String(value) === String(responseId));
		};

		const toggleResponse = (questionId, responseId, ignore) => {
			if (ignore) return;
			const question = questions.value.find((item) => String(item.id) === String(questionId));
			if (!question) return;

			const min = Number(question.min_responses || 1);
			let max = Number(question.max_responses || min || 1);
			if (!max || max < min) max = min || 1;

			const current = answers.value[questionId] ? [...answers.value[questionId]] : [];
			const existingIndex = current.findIndex((value) => String(value) === String(responseId));

			if (existingIndex >= 0) {
				current.splice(existingIndex, 1);
				answers.value = { ...answers.value, [questionId]: current };
				return;
			}

			if (max === 1) {
				answers.value = { ...answers.value, [questionId]: [responseId] };
				return;
			}

			if (current.length >= max) return;

			current.push(responseId);
			answers.value = { ...answers.value, [questionId]: current };
		};

		const nextQuestion = () => {
			if (!canContinue.value) return;

			if (isLastQuestion.value) {
				resolveQuiz();
				return;
			}

			currentIndex.value += 1;
		};

		const prevQuestion = () => {
			if (currentIndex.value === 0) return;
			currentIndex.value -= 1;
		};

		const resolveQuiz = async () => {
			if (!quiz.value) return;

			const invalidQuestion = questions.value.find((question) => !isQuestionValid(question));
			if (invalidQuestion) {
				errorMessage.value = "Please answer all questions using the required number of selections.";
				return;
			}

			loading.value = "Finding your ideas...";
			errorMessage.value = "";

			try {
				const info = await ApiService.post(`quiz/v2/${quiz.value.id}/resolve`, { answers: answers.value });
				const data = info && info.data ? info.data : {};
				results.value = Array.isArray(data.results) ? data.results : [];
				view.value = "results";
				general.posthogCapture("ideas-quiz-complete");
			} catch (err) {
				console.error("IdeaQuiz.resolve", err);
				errorMessage.value = "We could not calculate your results.";
			} finally {
				loading.value = false;
			}
		};

		const resetQuiz = () => {
			view.value = "quiz";
			results.value = [];
			currentIndex.value = 0;
			initializeAnswers(questions.value);
			errorMessage.value = "";
		};

		const goToPitch = () => {
			emit("close");
			router.push("/opportunities");
		};

		const openCollabGuide = () => {
			store.commit("general/openCollabHelpNeverDonePopup", {
				source: "idea-quiz",
			});
		};

		watch(
			() => props.quizId,
			(nextId) => {
				loadQuiz(nextId);
			},
			{ immediate: true }
		);

		return {
			loading,
			errorMessage,
			quizReady,
			questions,
			currentIndex,
			totalQuestions,
			currentQuestion,
			responsesForCurrent,
			selectionLabel,
			view,
			isLastQuestion,
			canContinue,
			hasResults,
			collabIds,
			legacyTitles,
			hasPitch,
			showCollabTypes,
			handleClose,
			nextQuestion,
			prevQuestion,
			toggleResponse,
			isSelected,
			resetQuiz,
			goToPitch,
			openCollabGuide,
		};
	},
};
</script>

<style scoped lang="less">
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";

.idea-quiz {
	display: flex;
	flex-direction: column;
	gap: 20px;
	color: @darkContrast;

	.quiz-header {
		h2 {
			margin: 0;
			font-size: 28px;
			font-weight: 700;
			color: @darkContrast;
		}

		p {
			margin: 4px 0 0;
			color: @textGrey;
			font-size: 15px;
		}
	}

	.quiz-error {
		padding: 10px 14px;
		border-radius: @radiusMd;
		background: fade(@formDefaultColour, 12%);
		color: @formDefaultColour;
		font-size: 14px;
		border: 1px solid fade(@formDefaultColour, 35%);
	}

	.quiz-body {
		display: flex;
		flex-direction: column;
		gap: 18px;
	}

	.quiz-question {
		background: #fff;
		border: 1px solid fade(@borderRoseBase, 40%);
		border-radius: @radiusXl;
		padding: 20px;
		.elevation.editorialSubtle();
	}

	.question-meta {
		display: flex;
		justify-content: space-between;
		color: @textGrey;
		font-size: 13px;
		margin-bottom: 10px;
	}

	.question-title {
		margin: 0 0 8px 0;
		font-size: 20px;
		color: @darkContrast;
	}

	.question-description {
		margin: 0 0 16px 0;
		color: fade(@darkContrast, 70%);
		font-size: 14px;
	}

	.responses {
		display: grid;
		gap: 12px;
	}

	.response-option {
		background: #fff;
		border: 1px solid fade(@borderRoseBase, 45%);
		border-radius: @radiusLg;
		padding: 10px 12px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		text-align: left;
		cursor: pointer;
		transition:
			transform 0.2s ease,
			border-color 0.2s ease,
			box-shadow 0.2s ease;

		&:not(.unsure) {

			&:hover {
				transform: translateY(-1px);
				box-shadow: 0 8px 16px fade(@borderRoseBase, 25%);
			}

		}

		&.selected {
			border-color: @formDefaultColour;
			background: fade(@formDefaultColour, 10%);
		}
		&.unsure {
			padding-top: 5px;
			padding-bottom: 5px;
			padding-right: 5px;
			cursor: default;
			.quiz-btn {
				padding: 6px;
				font-size: 0.7rem;
				margin-left: 10px;
			}
		}
	}

	.option-check {
		font-weight: 700;
		color: @formDefaultColour;
	}

	.quiz-results {
		background: #fff;
	}

	.results-header {
		margin-bottom: 16px;

		h3 {
			margin: 0 0 6px 0;
			font-size: 20px;
			color: @darkContrast;
		}

		p {
			margin: 0;
			color: fade(@darkContrast, 70%);
			font-size: 14px;
		}
	}

	.results-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-top: 18px;
	}

	.legacy-results {
		margin-top: 16px;
	}

	.legacy-title {
		margin: 0 0 8px 0;
		font-size: 14px;
		font-weight: 600;
		color: @darkContrast;
	}

	.legacy-tags {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.legacy-tag {
		background: fade(@formDefaultColour, 15%);
		color: @darkContrast;
		border-radius: @radiusPill;
		padding: 6px 12px;
		font-size: 12px;
		font-weight: 600;
	}

	.no-results {
		text-align: center;
		color: fade(@darkContrast, 70%);
		margin: 12px 0;
	}
}

.quiz-stepper {
	display: flex;
	gap: 8px;
	align-items: center;
	justify-content: center;
	display: none;

	.step-dot {
		width: 10px;
		height: 10px;
		border-radius: @radiusCircle;
		background: fade(@formDefaultColour, 25%);
	}

	.step-dot.done {
		background: fade(@formDefaultColour, 55%);
	}

	.step-dot.active {
		background: @formDefaultColour;
	}
}

.quiz-footer {
	display: flex;
	justify-content: space-between;
	gap: 12px;
}

.quiz-btn {
	margin: 0;
	padding: 12px 18px;
	font-size: 14px;
	border-radius: @radiusMd;

	&.primary {
		background: @formDefaultColour;
		color: #fff;
		border: none;

		&:hover {
			background: darken(@formDefaultColour, 6%);
		}
	}

	&.secondary {
		background: fade(@guidedRose, 12%);
		color: @guidedRose;
		border: 1px solid fade(@guidedRose, 25%);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

@media (max-width: 768px) {
	.idea-quiz {
		.question-meta {
			flex-direction: column;
			gap: 6px;
		}
	}
	.quiz-footer {
		.button {
			&:last-child {
				margin-right: 30px;
			}
		}
	}
	.quiz-stepper {
		display: none;
	}
}
</style>
